import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useJobs } from "@/lib/api/hooks";
import { StatusBadge } from "@/components/status-badge";
import { formatDuration, formatRelative, formatTokens } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { ArrowUpRight, Plus, Clock, Coins, Activity, ListChecks } from "lucide-react";
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

export const Route = createFileRoute("/_authenticated/dashboard")({
  component: DashboardPage,
  head: () => ({ meta: [{ title: "Dashboard — CSaaS" }] }),
});

function DashboardPage() {
  const { data: jobs = [] } = useJobs();

  const stats = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayJobs = jobs.filter((j) => new Date(j.createdAt) >= today);
    const tokens7d = jobs
      .filter((j) => Date.now() - new Date(j.createdAt).getTime() < 7 * 86400000)
      .reduce((sum, j) => sum + (j.tokensUsed ?? 0), 0);
    const completed = jobs.filter((j) => j.durationMs);
    const avg = completed.length
      ? completed.reduce((s, j) => s + (j.durationMs ?? 0), 0) / completed.length
      : 0;
    const queueDepth = jobs.filter((j) => j.status === "queued" || j.status === "running").length;
    return { todayCount: todayJobs.length, tokens7d, avgMs: avg, queueDepth };
  }, [jobs]);

  const series = useMemo(() => {
    const days: { day: string; tokens: number }[] = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date();
      d.setHours(0, 0, 0, 0);
      d.setDate(d.getDate() - i);
      const dayLabel = d.toLocaleDateString(undefined, { weekday: "short" });
      const tokens = jobs
        .filter((j) => {
          const t = new Date(j.createdAt);
          t.setHours(0, 0, 0, 0);
          return t.getTime() === d.getTime();
        })
        .reduce((s, j) => s + (j.tokensUsed ?? 0), 0);
      days.push({ day: dayLabel, tokens });
    }
    return days;
  }, [jobs]);

  const recent = jobs.slice(0, 6);

  return (
    <div className="space-y-8 max-w-7xl">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Overview of your skill executions and usage.
          </p>
        </div>
        <Button asChild>
          <Link to="/new">
            <Plus className="h-4 w-4" /> New job
          </Link>
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={<ListChecks className="h-4 w-4" />}
          label="Jobs today"
          value={String(stats.todayCount)}
        />
        <StatCard
          icon={<Coins className="h-4 w-4" />}
          label="Tokens (7d)"
          value={formatTokens(stats.tokens7d)}
        />
        <StatCard
          icon={<Clock className="h-4 w-4" />}
          label="Avg duration"
          value={formatDuration(stats.avgMs)}
        />
        <StatCard
          icon={<Activity className="h-4 w-4" />}
          label="Active queue"
          value={String(stats.queueDepth)}
        />
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base">Tokens used · last 7 days</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={series} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="tokenFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="var(--color-primary)" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="var(--color-primary)" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis
                    dataKey="day"
                    stroke="var(--color-muted-foreground)"
                    fontSize={11}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    stroke="var(--color-muted-foreground)"
                    fontSize={11}
                    tickLine={false}
                    axisLine={false}
                    width={40}
                  />
                  <Tooltip
                    contentStyle={{
                      background: "var(--color-popover)",
                      border: "1px solid var(--color-border)",
                      borderRadius: 8,
                      fontSize: 12,
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="tokens"
                    stroke="var(--color-primary)"
                    strokeWidth={2}
                    fill="url(#tokenFill)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base">Recent jobs</CardTitle>
            <Button asChild variant="ghost" size="sm" className="h-7 text-xs">
              <Link to="/jobs">
                View all <ArrowUpRight className="h-3 w-3" />
              </Link>
            </Button>
          </CardHeader>
          <CardContent className="space-y-2">
            {recent.map((j) => (
              <Link
                key={j.id}
                to="/jobs/$id"
                params={{ id: j.id }}
                className="flex items-center justify-between gap-2 rounded-md px-2 py-2 hover:bg-muted transition-colors"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{j.skillName}</div>
                  <div className="text-xs text-muted-foreground">{formatRelative(j.createdAt)}</div>
                </div>
                <StatusBadge status={j.status} />
              </Link>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function StatCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between text-muted-foreground text-xs">
          <span>{label}</span>
          {icon}
        </div>
        <div className="mt-2 font-display text-3xl font-semibold tracking-tight">{value}</div>
      </CardContent>
    </Card>
  );
}
